import '../../styles/CaseViewer.css';

import { Fragment, useState, useEffect } from 'react';

import { CaseReport, CaseReportSegment, CaseReportTotals } from '../../models/CaseTypes';
import { ChartDataItem } from '../../models/ChartTypes';
import { logTrace } from '../../utils/trackingUtils';
import CaseEfficiencyTimeline from './CaseEfficiencyTimeline';
import { Modal } from '../common/Modal';
import { BREAKPOINT_MOBILE, useWindowDimensions } from '../../utils/uiUtils';
import { formatTimeDisplay, getSegmentKeyByLabel } from '../../utils/caseUtils';
import CaseEfficiencyTotalsDesktop from './CaseEfficiencyTotalsDesktop';
import CaseEfficiencyTotalsMobile from './CaseEfficiencyTotalsMobile';
import CaseEfficiencyViewModel from '../../models/CaseEfficiencyViewModel';
import { VideoPlayer, VideoPlayerParams } from '../common/VideoPlayer';

interface TimeDataViewModel {
  segments: CaseReportSegment[];
  totals: CaseReportTotals;
}

const getSegmentsByType = (segments: CaseReportSegment[], type: string): CaseReportSegment[] => {
  return segments?.filter(segment => segment.segmentType === type) || [];
};

/**
 * Component to display the Case Efficiency data
 * This shows metrics about surgical efficiency such as time per step
 */
const CaseEfficiency = ({ caseReport }: { caseReport: CaseReport }):
  React.JSX.Element => {
  const [timeData, setTimeData] = useState<TimeDataViewModel|null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [videoSegment, setVideoSegment] = useState<VideoPlayerParams|null>(null);
  const [displayEfficiency, setDisplayEfficiency] = useState<boolean>(false);
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<CaseReportSegment|null>(null);
  const [showContentModal, setShowContentModal] = useState<boolean>(false);

  const windowWidth = useWindowDimensions().width;
  const viewModel = new CaseEfficiencyViewModel(caseReport);

  useEffect(() => {
    if (!caseReport) {
      return;
    }

    const timeData: TimeDataViewModel = {
      segments: caseReport.segments,
      totals: caseReport.totals
    };
    const hasData = timeData.segments &&
      timeData.segments.length > 0 &&
      timeData.totals.captured_operative_time > 0;

    setDisplayEfficiency(hasData);
    setTimeData(timeData);
    setChartData(viewModel.getActivityEfficiencyViewModel());

    setError('');
    setLoading(false);
  }, [caseReport]);

  /* Handles a click of the total time bar by displaying video from start of first segment to end of last segment */
  // NOTE: This behavior assumes that all segment videos are the same overall video with different start and end times.
  const handleTotalTimeClick = (segmentName: string): void => {
    if (!timeData || !timeData.segments || timeData.segments.length === 0) {
      return;
    }
    const activity_name = getSegmentKeyByLabel(segmentName);
    const segments = getSegmentsByType(timeData.segments, activity_name);

    // Order segments by segment start time
    const orderedSegments = segments.sort((a, b) => a.normalizedStartMin - b.normalizedStartMin);
    if (orderedSegments.length === 0) {
      return;
    }

    // NOTE: The duration of the video segment MAY be longer than the sum of the individual segments of this type.
    // This is because there may be other activities that occur interleaved with this activity.
    const firstSegment = orderedSegments[0];
    const lastSegment = orderedSegments[segments.length - 1];

    if (!firstSegment || !firstSegment.signedUrl || firstSegment.signedUrl.length === 0) {
      setVideoSegment(null);
      return;
    }

    logTrace('CaseEfficiency', 'proprio_bar_click_' + segmentName, segmentName);
    if (!firstSegment.videoIsValid || !lastSegment.videoIsValid) {
      return setShowContentModal(true);
    } else {
      setVideoSegment({
        videoUrl: firstSegment.signedUrl,
        startTimeSeconds: firstSegment.videoStartSec || 0,
        endTimeSeconds: lastSegment.videoEndSec || 0,
        segmentTitle: segmentName
      } as VideoPlayerParams);
    }
  };

  /**
   * Handle individual segment click from timeline
   * @param segment The segment that was clicked.
  */
  const handleSegmentClick = (segment: CaseReportSegment): void => {
    setSelectedSegment(segment);
    if (segment.videoIsValid) {
      return setVideoSegment(convertToVideoSegment(segment));
    } else {
      setShowContentModal(true);
    }
  };

  const convertToVideoSegment = (segment: CaseReportSegment): VideoPlayerParams|null => {
    if (
      !segment ||
      !segment.signedUrl ||
      segment.signedUrl.length === 0 ||
      !segment.videoIsValid ||
      !segment.videoStartSec ||
      !segment.videoEndSec
    ) {
      return null;
    }

    return {
      videoUrl: segment.signedUrl,
      startTimeSeconds: segment.videoStartSec,
      endTimeSeconds: segment.videoEndSec,
      segmentTitle: segment.segmentType
    } as VideoPlayerParams;
  };

  const handleCloseVideo = (): void => {
    if (videoSegment) {
      logTrace('CaseEfficiency', 'proprio_video_close_' + videoSegment.segmentTitle, videoSegment.segmentTitle);
    }
    setVideoSegment(null);
  };

  if (loading) {
    return <div className="loading">Loading efficiency data...</div>;
  }

  if (error) {
    return (
      <div className="case-graph-container">
        <div className="error-message">
          <p>Case efficiency data is not available.</p>
          <p className="error-details">{error}</p>
        </div>
      </div>
    );
  }

  if (!displayEfficiency) {
    return (
      <div className="case-graph-container">
        <div className="warning-message">
          <p>Case efficiency data is not available for this case.</p>
        </div>
      </div>
    );
  }

  const totalNavigatedTime = formatTimeDisplay(timeData?.totals?.total_navigated_time || 0);

  return (
    <Fragment>
      {/* Bar Chart Container */}
      <div className="case-graph-container">
        <div className="chart-container">
          {windowWidth <= BREAKPOINT_MOBILE ? (
            <CaseEfficiencyTotalsMobile chartData={chartData} onSegmentClick={handleTotalTimeClick} />
          ) : (
            <CaseEfficiencyTotalsDesktop
              chartData={chartData}
              onSegmentClick={handleTotalTimeClick}
            />
          )}
        </div>
      </div>

      {/* Timeline Container */}
      <CaseEfficiencyTimeline segments={(timeData?.segments) || []} totalTimeDisplay={totalNavigatedTime}
        onSegmentSelected={(segment: CaseReportSegment) => {
          handleSegmentClick(segment);
        } }/>

      {/* Add video player modal */}
      {videoSegment && (
        <Modal
          title={videoSegment.segmentTitle}
          onClose={handleCloseVideo}
        >
          <VideoPlayer
            key='segment-video-player'
            videoUrl={videoSegment.videoUrl}
            startTimeSeconds={videoSegment.startTimeSeconds}
            endTimeSeconds={videoSegment.endTimeSeconds}
            segmentTitle={videoSegment.segmentTitle}
          />
        </Modal>
      )}

      {showContentModal && (
        <Modal
          title={(selectedSegment?.segmentType || 'Segment') + ' Video Unavailable'}
          onClose={() => setShowContentModal(false)}
        >
          This segment does not have a video available.
        </Modal>
      )}
    </Fragment>
  );
};

export default CaseEfficiency;
